import React,{ useCallback, useMemo, useState } from "react";
import ReactSelect, { components } from "react-select";

// ==========================================
// Extracted Styles & Configuration
// ==========================================
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: state.isFocused ? "8px 8px 0 0" : "8px",
    boxShadow: "none",
    height: 55,
    "&:hover": {},
  }),
  option: (styles, { isDisabled }) => ({
    ...styles,
    color: isDisabled ? "#ccc" : styles.color,
    cursor: isDisabled ? "not-allowed" : "default",
  }),
};

const styles = {
  optionLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  valueSummary: {
    marginLeft: 8,
    fontSize: "1rem",
    fontFamily: "VodafoneRg-Bold",
    color: "#e74c3d",
  },
  tagContainer: {
    marginTop: 10,
    display: "flex",
    flexWrap: "wrap",
    gap: 5,
  },
  tag: {
    background: "#e0e0e0",
    padding: 5,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  removeButton: {
    marginLeft: 5,
    cursor: "pointer",
    border: "none",
    background: "none",
    margin: "auto 5px",
    fontSize: "1.4rem",
    lineHeight: 1,
  },
};

const MAX_CHALLENGE = 4;

// ==========================================
// Custom Sub-Components
// ==========================================
const Option = (props) => {
  const { isSelected, label, data } = props;
  const checkboxId = `selectMulti-${String(data?.value ?? label)}`;

  return (
    <components.Option {...props}>
      <label htmlFor={checkboxId} style={styles.optionLabel}>
        <input
          id={checkboxId}
          type="checkbox"
          checked={isSelected}
          onChange={() => {}}
          aria-label={label}
        />
        <span>{label}</span>
      </label>
    </components.Option>
  );
};

const ValueContainer = (props) => {
  const { children, getValue, selectProps } = props;
  const maxToShow = selectProps?.maxToShow ?? 3;
  const length = getValue()?.length || 0;

  return (
    <components.ValueContainer {...props}>
      {children}
      {!selectProps?.inputValue && length > maxToShow && (
        <span style={styles.valueSummary}>
          + {length - maxToShow} selected{" "}
          {selectProps?.mode === "CHALLENGE" ? "(Max 4 challenge)" : null}
        </span>
      )}
    </components.ValueContainer>
  );
};

// ==========================================
// Main Component
// ==========================================
const IdeaDropdownWithSearch = ({ dataList, sendDataToParent, mode }) => {
  const [selectedOptions, setSelectedOptions] = useState([]);

  const options = useMemo(
    () =>
      (dataList || []).map(({ id, value, title }) => ({
        value: id ?? value,
        label: title,
      })),
    [dataList]
  );

  const handleChange = useCallback(
    (nextSelected) => {
      const safeSelected = nextSelected || [];
      setSelectedOptions(safeSelected);
      sendDataToParent?.(safeSelected);
    },
    [sendDataToParent]
  );

  const handleRemove = useCallback(
    (valueToRemove) => {
      setSelectedOptions((prev) => {
        const updated = prev.filter((opt) => opt.value !== valueToRemove);
        sendDataToParent?.(updated);
        return updated;
      });
    },
    [sendDataToParent]
  );

  const isOptionDisabled = useCallback(
    (option) => {
      if (mode !== "CHALLENGE") return false;
      const alreadySelected = selectedOptions.some((s) => s.value === option.value);
      if (alreadySelected) return false;
      return selectedOptions.length >= MAX_CHALLENGE;
    },
    [mode, selectedOptions]
  );

  return (
    <div style={{marginBottom:'1rem'}}>
      <ReactSelect
        options={options}
        isMulti
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{ Option, ValueContainer }}
        onChange={handleChange}
        value={selectedOptions}
        styles={customSelectStyles}
        controlShouldRenderValue={false}
        maxToShow={0}
        isOptionDisabled={isOptionDisabled}
        mode={mode}
      />

      {/* Selected Tags Display Area */}
      <div style={styles.tagContainer}>
        {selectedOptions.map(({ value, label }) => (
          <span key={value} style={styles.tag}>
            {label}
            <button
              type="button"
              onClick={() => handleRemove(value)}
              style={styles.removeButton}
              aria-label={`Remove ${label}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

export default IdeaDropdownWithSearch;
